import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "page-shell px-4 mb-10" }
const _hoisted_2 = {
  key: 0,
  class: "section-card error-card"
}
const _hoisted_3 = { class: "mt-4" }

import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { fetchVendorAppointmentDetails } from "@/shared/services/appointments.api";
import AppointmentCustomerInfoCard from "@/shared/components/AppointmentCustomerInfoCard.vue";
import DetailPageSkeleton from "@/shared/components/DetailPageSkeleton.vue";
import AppointmentAssignedProfessionalCard from "@/modules/vendor/components/AppointmentAssignedProfessionalCard.vue";
import AppointmentScheduleDetailsCard from "@/modules/shared/components/AppointmentScheduleDetailsCard.vue";
import AppointmentRescheduleRequestCard from "@/modules/vendor/components/AppointmentRescheduleRequestCard.vue";
import AppointmentCancellationRequestCard from "@/modules/vendor/components/AppointmentCancellationRequestCard.vue";
import AppointmentPaymentSummary from "@/modules/shared/components/AppointmentPaymentSummary.vue";
import DetailPageHeader from "@/modules/shared/components/DetailPageHeader.vue";

type AppointmentDetail = {
  id: number | string;
  appointment_no: string;
  status: string;
  created_at: string;
  vendor_id?: number | string;
  client_id?: number | string;
  company_id?: number | string;
  cancellation_request: {
    is_requested: boolean;
    reason: string | null;
  };
  reschedule_request: {
    is_requested: boolean;
    preferred_date: string | null;
    preferred_time: string | null;
    reason: string | null;
  };
  service_provider?: {
    id: number | string;
    name: string;
    email: string;
    phone_no: string;
    avatar: string | null;
  } | null;
  customer_info: {
    id: number | string;
    name: string;
    email: string;
    phone_no: string;
    avatar: string | null;
  } | null;
  schedule_info: {
    appointment_date: string | null;
    appointment_time: string | null;
    note: string | null;
  } | null;
  service_address: {
    add1: string | null;
    add2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
  } | null;
  total_info: {
    is_paid: number | boolean;
    sub_total: string | number | null;
    tax_amount: string | number | null;
    total_amount: string | number | null;
  } | null;
  quotation: unknown;
  payment_info: {
    id: number | string;
    payment_code: string;
    amount: string | number;
    gateway: string;
    payment_type: string;
    card_last4: string | null;
    cardholder_name: string | null;
    transaction_id: string | null;
    ref_no: string | null;
    created_at: string;
  } | null;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'VendorAppontmentDetailPage',
  setup(__props) {

const route = useRoute();

const isLoading = ref(false);
const loadError = ref<string | null>(null);
const appointment = ref<AppointmentDetail>({
  id: 0,
  appointment_no: "",
  status: "",
  created_at: "",
  customer_info: null,
  schedule_info: null,
  service_address: null,
  total_info: null,
  quotation: null,
  payment_info: null,
  vendor_id: null,
  client_id: null,
  company_id: null,
  cancellation_request: {
    is_requested: false,
    reason: null,
  },
  reschedule_request: {
    is_requested: false,
    preferred_date: null,
    preferred_time: null,
    reason: null,
  },
});

async function loadAppointmentDetails() {
  const id = String(route.params.id ?? "");
  if (!id) return;
  try {
    isLoading.value = true;
    loadError.value = null;
    const response = await fetchVendorAppointmentDetails(id);
    const data = (response as any)?.data ?? response;
    if (data) {
      appointment.value = {
        id: data.id,
        appointment_no: String(data.appointment_no ?? ""),
        status: String(data.status ?? ""),
        created_at: String(data.created_at ?? ""),
        customer_info: data.customer_info ?? null,
        schedule_info: data.schedule_info ?? {
          appointment_date: null,
          appointment_time: null,
          note: null,
        },
        service_address: data.service_address ?? null,
        total_info: data.total_info ?? null,
        quotation: data.quotation ?? null,
        payment_info: data.payment_info ?? null,
        vendor_id: data.vendor_id ?? null,
        client_id: data.client_id ?? null,
        company_id: data.company_id ?? null,
        cancellation_request: data.cancellation_request ?? {
          is_requested: false,
          reason: null,
        },
        reschedule_request: data.reschedule_request ?? {
          is_requested: false,
          preferred_date: null,
          preferred_time: null,
          reason: null,
        },
        service_provider: data.service_provider
          ? {
              id: String(data.service_provider.id ?? ""),
              name: String(data.service_provider.name ?? ""),
              email: String(data.service_provider.email ?? ""),
              phone_no: String(data.service_provider.phone_no ?? ""),
              avatar: data.service_provider.avatar ?? null,
            }
          : null,
      };
    }
  } catch (error) {
    loadError.value = (error as any)?.message || "Failed to load appointment";
    // eslint-disable-next-line no-console
    console.error("fetchVendorAppointmentDetails failed", error);
  } finally {
    isLoading.value = false;
  }
}

async function handleAssignedProfessional() {
  // After assigning a professional, refresh appointment details from API.
  await loadAppointmentDetails();
}

onMounted(loadAppointmentDetails);

const appointmentTimelineItems: ReadonlyArray<{
  title: string;
  time: string;
  state: "done" | "active" | "pending";
}> = [
  { title: "Booking Confirmed", time: "1:45 PM", state: "done" },
  { title: "Payment Completed", time: "1:48 PM", state: "done" },
  { title: "Provider Assigned", time: "1:52 PM", state: "active" },
  { title: "On The Way", time: "-", state: "pending" },
  { title: "Arrived", time: "-", state: "pending" },
  { title: "Service Started", time: "1:45 PM", state: "pending" },
  { title: "Service Completed", time: "1:45 PM", state: "pending" },
];

return (_ctx: any,_cache: any) => {
  const _component_v_alert = _resolveComponent("v-alert")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createBlock(DetailPageSkeleton, {
          key: 0,
          count: 2
        }))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (loadError.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(_component_v_alert, {
                  type: "error",
                  variant: "tonal"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(loadError.value), 1)
                  ]),
                  _: 1
                })
              ]))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(DetailPageHeader, {
                    item: {
              title: { label: 'Appontment#', value: appointment.value.appointment_no },
              status: { label: 'Status', value: appointment.value.status },
            }
                  }, null, 8, ["item"])
                ]),
                _createElementVNode("div", null, [
                  _createVNode(AppointmentCustomerInfoCard, {
                    "customer-info": appointment.value.customer_info
                  }, null, 8, ["customer-info"])
                ]),
                (appointment.value.reschedule_request?.is_requested)
                  ? (_openBlock(), _createBlock(AppointmentRescheduleRequestCard, {
                      key: 0,
                      appointment: appointment.value
                    }, null, 8, ["appointment"]))
                  : _createCommentVNode("", true),
                (appointment.value.cancellation_request?.is_requested)
                  ? (_openBlock(), _createBlock(AppointmentCancellationRequestCard, {
                      key: 1,
                      appointment: appointment.value
                    }, null, 8, ["appointment"]))
                  : _createCommentVNode("", true),
                _createVNode(AppointmentScheduleDetailsCard, { appointment: appointment.value }, null, 8, ["appointment"]),
                _createVNode(AppointmentAssignedProfessionalCard, {
                  appointment: appointment.value,
                  onAssigned: _cache[0] || (_cache[0] = ($event: any) => (handleAssignedProfessional()))
                }, null, 8, ["appointment"]),
                _createVNode(AppointmentPaymentSummary, { appointment: appointment.value }, null, 8, ["appointment"])
              ], 64))
        ], 64))
  ]))
}
}

})